import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components"
import { PortOfEntryType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import Swal from "sweetalert2"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: PortOfEntryType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: PortOfEntryType) => void
  handleFormUpdate: (form: PortOfEntryType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const  PortOfEntry: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PortOfEntryType>()

  const onSubmit = (data: PortOfEntryType) => {
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      // color: "#ffffff",
      // background: "#333333",
      confirmButtonColor: "#a33333",
      cancelButtonColor: "#CEA16B",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: white;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: PortOfEntryType) => {
    props.setTitle('Update Port of Entry')
    setValue("id", e.id)
    setValue("name", e.name)
    setValue("city", e.city)
    setValue("state", e.state)
    setValue("zipCode", e.zipCode)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      name: undefined,
      city: undefined,
      state: undefined,
      zipCode: undefined,
      id: undefined,
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Name</label>
                <input
                  className={
                    (errors.name ? "border-red-400" : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Name"
                  {...register("name", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">City</label>
                <input
                  className={
                    (errors.city ? "border-red-400" : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter City"
                  {...register("city", { required: true })}
                />
              </fieldset>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">State</label>
                <input
                  className={
                    (errors.state ? "border-red-400" : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter State"
                  {...register("state", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Zip Code</label>
                <input
                  className={
                    (errors.zipCode ? "border-red-400" : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Zipcode"
                  {...register("zipCode", { required: true })}
                />
              </fieldset>
            </div>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>{e.name}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>{e.city}</td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                <p>{e.state}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                <p>{e.zipCode}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 dark:text-primary-8 text-light-1 cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 dark:text-primary-8 text-light-1 cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
